import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import ProfileLayout from "../components/ProfileLayout";
import { ArrowDownLeft, ArrowUpRight, Wallet } from "lucide-react";
import { clientTimeZone, formatLocalTimestamp } from "../lib/dateLocal";
import { formatUsd } from "../lib/currencyDisplay";

const TYPE_LABEL = {
  booking_payment: "Trip payment",
  mileage_overage: "Mileage overage",
  escrow_in: "Escrow in",
  escrow_refund: "Escrow refund",
  booking_refund: "Trip refund",
  trip_amend_adjustment: "Trip change adjustment",
  escrow_adjustment: "Escrow adjustment",
  mileage_overage_escrow: "Mileage overage (escrow)",
  cancellation_retention: "Cancellation fee",
  payout: "Trip payout",
  parking_escrow_in: "Parking escrow in",
  parking_escrow_refund: "Parking refund",
  parking_payout: "Parking payout",
  fractional_purchase: "Fraction purchase",
  fractional_sale: "Fraction sale",
  fractional_yield: "Investor yield",
  host_payout_fractional: "Fractional host payout",
};

function txnAmount(amount) {
  const n = Number(amount);
  return Number.isFinite(n) ? n : 0;
}

export default function ProfileTransactions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API}/wallet/me`, { withCredentials: true });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.detail || "Could not load transaction history");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const wallet = data?.wallet || {};
  const transactions = Array.isArray(data?.transactions) ? data.transactions : [];
  const displayTz = data?.display_tz || clientTimeZone();

  return (
    <ProfileLayout eyebrow="Profile" title="Transaction history">
      {loading && (
        <p className="text-sm text-[var(--zc-text-3)] mb-6" data-testid="profile-tx-loading">
          Loading wallet…
        </p>
      )}
      {error && (
        <div
          className="mb-6 p-3 rounded-lg border border-[rgba(245,158,11,0.35)] bg-[rgba(245,158,11,0.08)] text-sm text-[var(--zc-text-2)]"
          data-testid="profile-tx-error"
        >
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="zc-card p-5">
          <div className="flex items-center gap-2 eyebrow"><Wallet size={12} /> Available</div>
          <div className="zc-price text-3xl mt-2" data-testid="profile-tx-available">
            ${formatUsd(wallet.fiat_balance)}
          </div>
        </div>
        <div className="zc-card p-5">
          <div className="eyebrow">In escrow</div>
          <div className="zc-price text-3xl mt-2">${formatUsd(wallet.escrow_balance)}</div>
        </div>
        <div className="zc-card p-5">
          <div className="eyebrow">Lifetime earnings</div>
          <div className="zc-price text-3xl mt-2">${formatUsd(wallet.lifetime_earnings)}</div>
        </div>
      </div>

      <div className="zc-card overflow-hidden overflow-x-auto zc-stack-table" data-testid="profile-tx-table">
        <div className="hidden md:grid md:grid-cols-[24px,1fr,1fr,auto] zc-stack-head items-center gap-3 px-4 sm:px-6 py-3 text-xs uppercase tracking-widest text-[var(--zc-text-3)] border-b border-[var(--zc-border)] font-semibold">
          <div></div><div>Type</div><div>Booking</div><div>Amount</div>
        </div>
        {!loading && transactions.length === 0 && (
          <div className="px-6 py-12 text-center text-[var(--zc-text-3)] text-sm">No transactions yet.</div>
        )}
        {transactions.map((t, i) => {
          const amount = txnAmount(t.amount);
          return (
            <div
              key={t.txn_id || `${t.type}-${t.created_at}-${i}`}
              className="zc-stack-row md:grid md:grid-cols-[24px,1fr,1fr,auto] md:items-center gap-3 px-4 sm:px-6 py-3 border-b border-[var(--zc-border)] last:border-0 text-sm"
            >
              <div data-label="">
                {amount >= 0
                  ? <ArrowDownLeft size={14} className="text-[var(--zc-success)]" />
                  : <ArrowUpRight size={14} className="text-[var(--zc-text-2)]" />}
              </div>
              <div data-label="Type">
                <div className="capitalize font-semibold">{TYPE_LABEL[t.type] || (t.type || "transaction").replace(/_/g, " ")}</div>
                <div className="text-xs text-[var(--zc-text-3)]">{formatLocalTimestamp(t.created_at, displayTz)}</div>
              </div>
              <div data-label="Booking" className="font-mono text-xs text-[var(--zc-text-2)] break-all">
                {t.booking_id || t.offering_id || "—"}
              </div>
              <div data-label="Amount" className={"font-semibold " + (amount >= 0 ? "text-[var(--zc-success)]" : "text-[var(--zc-text-2)]")}>
                {amount >= 0 ? "+" : ""}${formatUsd(Math.abs(amount))}
              </div>
            </div>
          );
        })}
      </div>
    </ProfileLayout>
  );
}
